import { useTranslation } from 'next-i18next'
import { makeStyles, Container, Grid } from '@material-ui/core'
import T from './tokens'
import Reveal from './Reveal'

// The backend (/api/blog/fetch -> oldShippop /get/feed/wordpress/) flattens
// the WordPress REST shape into plain fields (link/img/title/desc — see
// components/home/News.js for the fields the original relies on). It isn't
// reachable from local dev, so the exact date field name it returns can't be
// confirmed empirically; try the common candidates in priority order and
// degrade gracefully (no "Update" row) if none are present.
const DATE_FIELD_CANDIDATES = [
  'date',
  'pubDate',
  'publish_date',
  'publishDate',
  'created_at',
  'createdAt',
  'update_date',
  'updateDate',
  'updated_at',
  'updatedAt',
  'modified',
  'post_date',
]

const getDateValue = (item) => {
  for (const field of DATE_FIELD_CANDIDATES) {
    if (item[field]) return item[field]
  }
  return null
}

const formatDate = (value) => {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(d)
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '70px 0',
    background: T.color.surface,
    [theme.breakpoints.down('sm')]: {
      padding: '40px 0',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: 32,
    flexWrap: 'wrap',
    gap: 16,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 20,
    },
  },
  heading: {
    fontFamily: T.font.heading,
    fontSize: 28,
    fontWeight: 500,
    color: T.color.ink,
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      fontSize: 22,
    },
  },
  seeAll: {
    fontFamily: T.font.body,
    fontSize: 15,
    color: T.color.brand,
    textDecoration: 'none',
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  '@keyframes shine': {
    '0%': { transform: 'translateX(-160%) skewX(-20deg)' },
    '100%': { transform: 'translateX(240%) skewX(-20deg)' },
  },
  card: {
    background: T.color.surface,
    borderRadius: T.radius.lg,
    overflow: 'hidden',
    boxShadow: T.shadow.card,
    height: '100%',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    textDecoration: 'none',
    display: 'block',
    position: 'relative',
    // Glare sweep across the card on hover (React Bits "Spotlight/Glare" vibe).
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '60%',
      height: '100%',
      background:
        'linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)',
      transform: 'translateX(-160%) skewX(-20deg)',
      pointerEvents: 'none',
      zIndex: 2,
    },
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: T.shadow.lg,
    },
    '&:hover::after': {
      animation: '$shine 0.9s ease',
    },
    '@media (prefers-reduced-motion: reduce)': {
      '&:hover::after': { animation: 'none' },
    },
  },
  cardImgWrap: {
    position: 'relative',
    width: '100%',
    paddingTop: '58%',
    background: T.color.canvasAlt,
    overflow: 'hidden',
  },
  cardImg: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  // Branded fallback shown when an article has no featured image, so every
  // card keeps a consistent thumbnail area instead of a blank stretch.
  defaultThumb: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${T.color.canvasAlt} 0%, #CFE6FF 100%)`,
    '& img': {
      width: '46%',
      maxWidth: 150,
      height: 'auto',
      opacity: 0.92,
    },
  },
  cardBody: {
    padding: '16px 20px 20px',
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
    gap: 12,
  },
  updateBadge: {
    fontFamily: T.font.body,
    fontSize: 12,
    fontWeight: 500,
    color: T.color.muted,
    background: T.color.canvas,
    padding: '4px 12px',
    borderRadius: T.radius.pill,
    whiteSpace: 'nowrap',
  },
  dateText: {
    fontFamily: T.font.body,
    fontSize: 13,
    color: T.color.faint,
    whiteSpace: 'nowrap',
  },
  cardTitle: {
    fontFamily: T.font.heading,
    fontSize: 16,
    fontWeight: 500,
    color: T.color.ink,
    margin: '0 0 8px',
    lineHeight: 1.4,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  cardDesc: {
    fontFamily: T.font.body,
    fontSize: 13,
    color: T.color.muted,
    lineHeight: 1.6,
    margin: 0,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  empty: {
    fontFamily: T.font.body,
    fontSize: 14,
    color: T.color.muted,
    textAlign: 'center',
    padding: '40px 0',
    '& a': {
      color: T.color.brand,
    },
  },
}))

const NewsCard = ({ item }) => {
  const classes = useStyles()
  const dateValue = getDateValue(item)
  const formattedDate = dateValue ? formatDate(dateValue) : null

  return (
    <a
      href={item.link}
      target="_blank"
      rel="noopener noreferrer"
      className={classes.card}
    >
      <div className={classes.cardImgWrap}>
        {item.img ? (
          <img
            src={item.img}
            alt={item.title}
            className={classes.cardImg}
            width={100}
            height={58}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className={classes.defaultThumb}>
            <img
              src="/static/img/sp-logo.svg"
              alt={item.title}
              width="150"
              height="27"
              loading="lazy"
              decoding="async"
            />
          </div>
        )}
      </div>
      <div className={classes.cardBody}>
        <div className={classes.metaRow}>
          <span className={classes.updateBadge}>Update</span>
          {formattedDate && <span className={classes.dateText}>{formattedDate}</span>}
        </div>
        <h3 className={classes.cardTitle}>{item.title}</h3>
        {item.desc && <p className={classes.cardDesc}>{item.desc}</p>}
      </div>
    </a>
  )
}

const NewsNew = ({ news = [] }) => {
  const classes = useStyles()
  const { t } = useTranslation('home')
  const displayNews = news.slice(0, 3)

  return (
    <section className={classes.root}>
      <Container maxWidth={false} style={{ maxWidth: T.container }}>
        <Reveal>
          <div className={classes.header}>
            <h2 className={classes.heading}>{t('newsSection.heading')}</h2>
            <a
              href="https://blog.shippop.com"
              target="_blank"
              rel="noopener noreferrer"
              className={classes.seeAll}
            >
              {t('newsSection.seeMore')}
            </a>
          </div>
        </Reveal>
        {displayNews.length > 0 ? (
          <Grid container spacing={3}>
            {displayNews.map((item, i) => (
              <Grid item xs={12} sm={6} md={4} key={item.link || i}>
                <Reveal delay={i * 0.12}>
                  <NewsCard item={item} />
                </Reveal>
              </Grid>
            ))}
          </Grid>
        ) : (
          <p className={classes.empty}>
            {t('newsSection.emptyPrefix')}{' '}
            <a href="https://blog.shippop.com" target="_blank" rel="noopener noreferrer">
              blog.shippop.com
            </a>
          </p>
        )}
      </Container>
    </section>
  )
}

export default NewsNew
